/**
 * 飞出卡片(rail 收起态 hover 弹出,亦可独立挂任意 nvc-rel 容器)。
 * 像素/结构真相源:handoff core.jsx FlyoutCard(227-243)/flySubText(219-224)
 * /PanelBody(205-217)/MainButtons(164-202)/ActivityCapsule(122-130),类名 nvc- 前缀。
 * 纯展示组件:子视图 view 是唯一局部 state,所有动作经 props 回调上抛。
 * 注意:flyout 内 Clock Out 直接执行无二次确认、activity 胶囊计时用 fmtHMS
 * (Row 用 fmtClip)—— 两处与 ClockActionRow 口径不同是 prototype 原义。
 * "more" 子视图嵌入 ClockMoreMenu(宿主 moreActions 有值时激活,只读态可用)。
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { BriefcaseBusiness, Clock, Coffee, MoreHorizontal, Play, Square, X } from "lucide-react";
import "./clock-panel.css";
import { fmtHMS, formatHm } from "./derive";
import type { ClockActionRowProps } from "./ClockActionRow";
import { BreakPicker, WorkTypePicker } from "./pickers";
import { ClockMoreMenu } from "./ClockMoreMenu";

export interface FlyoutCardProps extends ClockActionRowProps {
  /** firstClockInAt 的 "9:02 AM" 标签(sinceLabel 产物),无则 null。 */
  sinceText: string | null;
  /** 当前 break 类型名("Lunch"…,deriveBreakTypeLabel 产物),无则省略。 */
  breakTypeLabel?: string | null;
  onClose: () => void;
  /** 定位变体类,rail 形态传 "rail"(left:calc(100%+12px); bottom:-4px)。 */
  className?: string;
}

type ViewKind = "main" | "break" | "work" | "more";

/** flySubText 同构(core.jsx 219-224;break 类型名取自 sessions 流的 break_start.notes)。 */
function subText(props: FlyoutCardProps, t: (key: string, opts?: Record<string, unknown>) => string): string {
  const { displayMode, tick, sinceText, breakTypeLabel } = props;
  if (displayMode === "loading") return t("attendanceClock.loading");
  if (displayMode === "working" || displayMode === "activity") {
    // "Working · since {since} · Break {breakTime}"
    return `Working · since ${sinceText ?? "—"} · Break ${formatHm(tick.breakTodaySec)}`;
  }
  if (displayMode === "break") {
    // "On break · {breakType} · Worked {workTime}" or "On break · Worked {workTime}"
    return `On break${breakTypeLabel ? " · " + breakTypeLabel : ""} · Worked ${formatHm(tick.workSec)}`;
  }
  if (displayMode === "ended") return `${t("attendanceClock.shiftEnded")} · Break ${formatHm(tick.breakTodaySec)} today`;
  return t("attendanceClock.notClockedIn");
}

export function FlyoutCard(props: FlyoutCardProps) {
  const { t } = useTranslation("shifts");
  const {
    displayMode,
    tick,
    activityName,
    actionsAllowed,
    pending,
    workTypeOptions,
    workTypeLoading,
    onClockIn,
    onClockOut,
    onStartBreak,
    onEndBreak,
    onStartWorkType,
    onEndWorkType,
    moreActions,
    onClose,
    className,
  } = props;

  const [view, setView] = useState<ViewKind>("main");
  const busy = pending != null;

  // 状态变化(动作生效/他端打卡)→ 子视图复位(PanelBody 207 行)
  useEffect(() => {
    setView("main");
  }, [displayMode]);

  // ── 大计时口径(core.jsx FlyoutCard 229-232) ──────────────
  const big =
    displayMode === "loading"
      ? "—:—:—"
      : displayMode === "idle"
        ? "00:00:00"
        : displayMode === "break"
          ? fmtHMS(tick.curBreakSec)
          : displayMode === "ended"
            ? formatHm(tick.workSec)
            : fmtHMS(tick.workSec);
  const bigCls =
    displayMode === "break"
      ? "amber"
      : displayMode === "idle" || displayMode === "ended" || displayMode === "loading"
        ? "gray"
        : "green";

  // ── body(PanelBody 语义,meta=false;只读时整体不渲染) ────
  let body: React.ReactNode = null;
  if (view === "more" && moreActions) {
    body = <ClockMoreMenu actions={moreActions} onClose={() => setView("main")} />;
  } else if (actionsAllowed && displayMode !== "loading") {
    // loading(首拉未完成):body 留空,绝不渲染 clock-in/out 等动作按钮(防时序误点)
    if (view === "break") {
      body = (
        <BreakPicker
          disabled={busy}
          onBack={() => setView("main")}
          onPick={(t) => {
            onStartBreak(t);
            setView("main");
          }}
        />
      );
    } else if (view === "work") {
      body = (
        <WorkTypePicker
          options={workTypeOptions}
          loading={workTypeLoading}
          disabled={busy}
          onBack={() => setView("main")}
          onStart={(code, label, notes) => {
            onStartWorkType(code, label, notes);
            setView("main");
          }}
        />
      );
    } else {
      // main(MainButtons 164-202 行逐行对应)
      let buttons: React.ReactNode;
      if (displayMode === "idle" || displayMode === "ended") {
        buttons = (
          <div className="nvc-grid2">
            <button className="nvc-btn green full" onClick={onClockIn} disabled={busy}>
              <Play size={10} fill="currentColor" />
              {t("attendanceClock.clockIn")}
              <span className="bt">
                {displayMode === "ended" ? formatHm(tick.workSec) : "00:00:00"}
              </span>
            </button>
          </div>
        );
      } else if (displayMode === "working") {
        buttons = (
          <div className="nvc-grid2">
            <button className="nvc-btn" onClick={onClockOut} disabled={busy}>
              <Square size={9} fill="currentColor" />
              {t("attendanceClock.clockOut")}
            </button>
            <button className="nvc-btn" onClick={() => setView("break")} disabled={busy}>
              <Coffee size={13} />
              {t("attendanceClock.break")}
            </button>
            <button className="nvc-btn full" onClick={() => setView("work")} disabled={busy}>
              <BriefcaseBusiness size={13} />
              {t("attendanceClock.workType")}
            </button>
          </div>
        );
      } else if (displayMode === "activity") {
        buttons = (
          <>
            <div className="nvc-activity">
              <span className="nm">
                <BriefcaseBusiness size={12} />
                {activityName}
              </span>
              <span className="tm">{fmtHMS(tick.actSec)}</span>
              <button className="stop" title={t("attendanceClock.endActivity")} onClick={onEndWorkType} disabled={busy}>
                <Square size={9} fill="currentColor" />
              </button>
            </div>
            <div className="nvc-grid2">
              <button className="nvc-btn" disabled>
                <Square size={9} fill="currentColor" />
                {t("attendanceClock.clockOut")}
              </button>
              <button className="nvc-btn" disabled>
                <Coffee size={13} />
                {t("attendanceClock.break")}
              </button>
            </div>
            <div className="nvc-hint">{t("attendanceClock.endCurrentActivityFirst")}</div>
          </>
        );
      } else {
        // break
        buttons = (
          <>
            <div className="nvc-grid2">
              <button className="nvc-btn green" onClick={onEndBreak} disabled={busy}>
                <Play size={10} fill="currentColor" />
                {t("attendanceClock.endBreak")}
              </button>
              <button className="nvc-btn" disabled>
                <Square size={9} fill="currentColor" />
                {t("attendanceClock.clockOut")}
              </button>
            </div>
            <div className="nvc-hint">{t("attendanceClock.endBreakBeforeClockOut")}</div>
          </>
        );
      }
      body = <div className="nvc-body">{buttons}</div>;
    }
  }

  return (
    <div className={"nvc-flyout " + (className ?? "")}>
      <div className="nvc-fly-head">
        <Clock size={13} />
        <span>{t("attendanceClock.timeClock")}</span>
        {moreActions ? (
          <>
            <button
              className="nvc-x"
              title={t("attendanceClock.moreActions")}
              onClick={() => setView((v) => (v === "more" ? "main" : "more"))}
            >
              <MoreHorizontal size={13} />
            </button>
            <button className="nvc-x" style={{ marginLeft: 0 }} onClick={onClose} title={t("attendanceClock.close")}>
              <X size={12} />
            </button>
          </>
        ) : (
          <button className="nvc-x" onClick={onClose} title={t("attendanceClock.close")}>
            <X size={12} />
          </button>
        )}
      </div>
      <div className="nvc-bigtimer">
        <div className={"num " + bigCls}>{big}</div>
        <div className="sub">{subText(props, t)}</div>
      </div>
      {body}
    </div>
  );
}
